import React from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getGoogleClientId } from '@/lib/googleClientId';
import { toast } from 'sonner';
import { resolveOAuthErrorCopy } from '@/lib/oauthError';

/**
 * Why this dialog has a variant (Finding R2-3, review follow-up):
 *
 * The default copy says the connection "expired" and that the user "just needs to click
 * through Google's authorization screen". For an account that reached us WITHOUT the
 * Merchant API scope, both halves are wrong and the second one is actively harmful: it
 * describes exactly the behaviour that produced the bug. Nothing expired, and clicking
 * through without stopping at the checkbox reproduces the same broken state.
 *
 * The Settings account card already gets this right by accident: it wires its CTA
 * straight to GoogleOAuthButton with no dialog in between, so no wrong words are shown.
 * Rather than copy that (which would drop `login_hint` and reconnect an arbitrary Google
 * account, while the banner names one specific address), the dialog learns the reason.
 */
export type ReauthReason = 'expired' | 'scope_missing';

interface ReauthDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  googleAccountEmail?: string;
  googleAccountId: string;
  /** Defaults to 'expired', the pre-existing behaviour for every legacy caller. */
  reason?: ReauthReason;
}

const COPY: Record<ReauthReason, {
  title: string;
  description: string;
  instruction: string;
  cta: string;
}> = {
  expired: {
    title: 'reauth.title',
    description: 'reauth.description',
    instruction: 'reauth.instruction',
    cta: 'reauth.reconnect',
  },
  scope_missing: {
    title: 'reauth.scopeTitle',
    description: 'reauth.scopeDescription',
    instruction: 'reauth.scopeInstruction',
    cta: 'reauth.scopeGrant',
  },
};

const ReauthDialog: React.FC<ReauthDialogProps> = ({
  open,
  onClose,
  onSuccess,
  googleAccountEmail,
  googleAccountId,
  reason = 'expired',
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [reconnecting, setReconnecting] = React.useState(false);
  const copy = COPY[reason];

  const handleReconnect = async () => {
    if (!user) return;
    
    setReconnecting(true);
    
    // Open popup immediately (synchronously) to prevent browser blocking
    const popup = window.open(
      'about:blank',
      'google-reauth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    try {
      // Client-ID via gemeinsamen Helper (Retry + localStorage-Fallback, Sentry DENO-T)
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const clientId = await getGoogleClientId();
      const scope = [
        'https://www.googleapis.com/auth/content',
        'openid',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ].join(' ');

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', user.id);
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('include_granted_scopes', 'true');
      // Add the account ID to force reconnect to the same account
      if (googleAccountEmail) {
        authUrl.searchParams.set('login_hint', googleAccountEmail);
      }

      // Navigate the pre-opened popup or fallback to full-tab redirect
      if (popup && !popup.closed) {
        popup.location.href = authUrl.toString();
      } else {
        window.location.href = authUrl.toString();
        return;
      }

      // Listen for OAuth callback
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          popup?.close();
          toast.success(t('reauth.reconnectedSuccess'), {
            description: t('reauth.reconnectedDesc'),
          });
          window.removeEventListener('message', handleMessage);
          onSuccess();
          onClose();
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          popup?.close();
          // Finding R2-3: this dialog is where the scope-missing banner CTA lands. If the
          // user clicks through consent a second time and still leaves the Merchant API
          // checkbox unticked, "reconnection failed" would be misleading; the connection
          // worked, the permission did not.
          // Finding R3-3 adds the same argument for a failed profile lookup: a Google
          // outage is not a failed reconnection either, and telling the user to
          // "reconnect" would send them into a loop that cannot succeed until Google
          // recovers.
          // The description comes from our own catalogue, not from the postMessage
          // payload: GoogleCallback always populates event.data.error, so an
          // `event.data.error || …` fallback would be unreachable code that later readers
          // would mistake for a working default.
          const copy = resolveOAuthErrorCopy(event.data.code, t);
          toast.error(
            copy?.title ?? t('reauth.reconnectionFailed'),
            {
              description: copy?.description ?? (event.data.error || t('reauth.reconnectionFailedDesc')),
            },
          );
          window.removeEventListener('message', handleMessage);
          setReconnecting(false);
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setReconnecting(false);
        }
      }, 1000);

    } catch (error) {
      console.error('Error reconnecting Google account:', error);
      Sentry.captureException(error, { tags: { feature: 'auth', action: 'reconnect-google' } });
      toast.error(t('status.error'), {
        description: t('reauth.initFailed'),
      });
      setReconnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <DialogTitle>{t(copy.title)}</DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="space-y-3">
              <span className="block">
                {t(copy.description)}
              </span>
              {googleAccountEmail && (
                <span className="block font-medium">
                  {t('reauth.account')} <span className="text-foreground">{googleAccountEmail}</span>
                </span>
              )}
              <span className="block text-sm">
                {t(copy.instruction)}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 justify-end mt-4">
          <Button variant="outline" onClick={onClose} disabled={reconnecting}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleReconnect} disabled={reconnecting}>
            {reconnecting ? t('reauth.reconnecting') : t(copy.cta)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReauthDialog;
